export interface CookieOptions {
  /** Lifetime in seconds. When omitted the cookie is a session cookie. */
  maxAge?: number;
  /** SameSite policy. Defaults to `Strict`. */
  sameSite?: 'Strict' | 'Lax' | 'None';
  /**
   * Domain attribute. Set e.g. to `.engie.fr` to share the cookie with
   * sibling subdomains (the shell SPA). When omitted the cookie is scoped to
   * the current host only.
   */
  domain?: string;
}

/**
 * Set a cookie.
 * @param name - Cookie name
 * @param value - Cookie value
 * @param options - Optional attributes (maxAge, sameSite, domain)
 */
export const setCookie = (
  name: string,
  value: string,
  options: CookieOptions = {},
): void => {
  const { maxAge, sameSite = 'Strict', domain } = options;
  const maxAgePart = maxAge != null ? `; max-age=${maxAge}` : '';
  const domainPart = domain ? `; domain=${domain}` : '';
  // biome-ignore lint/suspicious/noDocumentCookie: Cookie Store API not widely supported, document.cookie required for cross-browser compatibility
  document.cookie = `${name}=${value}; path=/${maxAgePart}${domainPart}; SameSite=${sameSite}`;
};

/**
 * Get a cookie value
 * @param name - Cookie name
 * @returns Cookie value or null if not found
 */
export const getCookie = (name: string): string | null => {
  const cookies = document.cookie.split(';');
  for (const cookie of cookies) {
    const [cookieName, cookieValue] = cookie.trim().split('=');
    if (cookieName === name) {
      return cookieValue ?? null;
    }
  }
  return null;
};

/**
 * Delete a cookie
 * @param name - Cookie name
 */
export const deleteCookie = (name: string): void => {
  // biome-ignore lint/suspicious/noDocumentCookie: Cookie Store API not widely supported, document.cookie required for cross-browser compatibility
  document.cookie = `${name}=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT; SameSite=Strict`;
};
